import { useEffect, useState } from 'react';
import { defaultStage, stageLabel } from '@wr/shared';
import { get } from '../../api.js';
import { LISTS } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import type { Stage } from './data.js';

/**
 * The deal stages (brief section 4): read from HubSpot through the portal,
 * kept on the device for the next time without network; the portal's
 * provisional list (config/portal.json) until HubSpot has answered once.
 */
export function useStages(): Stage[] {
  const { store } = useOffline();
  const [stages, setStages] = useState<Stage[]>(LISTS.dealStagesFallback);
  useEffect(() => {
    let current = true;
    void (async () => {
      const kept = await store?.getMeta<Stage[]>('dealStages').catch(() => null);
      if (kept && current) setStages(kept);
      try {
        const { items } = await get<{ items: Stage[] }>('/leads/stages');
        if (!current) return;
        setStages(items);
        await store?.setMeta('dealStages', items);
      } catch {
        // No network: the stages kept, or the provisional list.
      }
    })();
    return () => {
      current = false;
    };
  }, [store]);
  return stages;
}

/** A stage's name, whichever list its ID comes from. */
export function stageName(id: unknown, stages: Stage[]): string {
  return stages.find((s) => s.id === id)?.label ?? stageLabel(id, LISTS);
}

/**
 * Qualified: MQLs; disqualified: Marketing nurturing; the stage HubSpot's
 * ID names (marked by the portal), else found by name among the stages.
 */
export function stageFor(qualification: unknown, stages: Stage[]): string | null {
  const fallback = defaultStage(qualification, LISTS);
  if (!fallback) return null;
  const marked = stages.find((s) => s.default === qualification);
  if (marked) return marked.id;
  const label = stageLabel(fallback, LISTS).toLowerCase();
  return stages.find((s) => s.label.toLowerCase() === label)?.id ?? fallback;
}
